"use client";

import { useEffect } from "react";

/**
 * Run `check` every `intervalMs` while the tab is visible, plus once on
 * visibilitychange so a woken laptop catches up immediately instead of
 * waiting out the interval. Shared by <UpdateNotice/> and <MaintenanceHold/>
 * — one copy of the interval/visibility scaffolding, each caller brings its
 * own check.
 *
 * `check` must be referentially stable (useCallback or module-level) — it is
 * an effect dependency, and an unstable one re-arms the interval per render.
 * Errors are the caller's to swallow: a poll helper has no better idea than
 * the caller what a failed check means.
 */
export function usePollWhileVisible(
  check: () => void | Promise<void>,
  intervalMs: number,
  { immediate = false }: { immediate?: boolean } = {},
): void {
  useEffect(() => {
    const run = () => {
      if (!document.hidden) void check();
    };
    if (immediate) run();
    const interval = setInterval(run, intervalMs);
    document.addEventListener("visibilitychange", run);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", run);
    };
  }, [check, intervalMs, immediate]);
}
